import { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Search, Pencil, Plus, Clock, UserX, UserCheck, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

export default function Vendedores() {
  const { isAuditor } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingVendor, setEditingVendor] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  
  const [horaEntrada, setHoraEntrada] = useState('');
  const [horaDescansoSalida, setHoraDescansoSalida] = useState('');
  const [horaDescansoVuelta, setHoraDescansoVuelta] = useState('');
  const [horaSalida, setHoraSalida] = useState('');
  const [conducta, setConducta] = useState('');
  const [llamadas, setLlamadas] = useState('');
  const [calidadAtencion, setCalidadAtencion] = useState('');

  const [editNombre, setEditNombre] = useState('');
  const [editFicha, setEditFicha] = useState('');

  const [nombre, setNombre] = useState('');
  const [apellido, setApellido] = useState('');
  const [fichaCasmu, setFichaCasmu] = useState('');
  const [newHoraEntrada, setNewHoraEntrada] = useState('08:00');
  const [newHoraDescansoSalida, setNewHoraDescansoSalida] = useState('12:00');
  const [newHoraDescansoVuelta, setNewHoraDescansoVuelta] = useState('13:00');
  const [newHoraSalida, setNewHoraSalida] = useState('17:00');

  const fetchVendors = async () => {
    try { const data = await api.getVendors(search); setVendors(data); }
    catch (error) { /* handled */ }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchVendors(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const openEditDialog = (vendor) => {
    setEditingVendor(vendor); setEditNombre(vendor.nombre || ''); setEditFicha(vendor.ficha_casmu || '');
    setHoraEntrada(vendor.hora_entrada || ''); setHoraDescansoSalida(vendor.hora_descanso_salida || '');
    setHoraDescansoVuelta(vendor.hora_descanso_vuelta || ''); setHoraSalida(vendor.hora_salida || '');
    setConducta(vendor.conducta || ''); setLlamadas(vendor.llamadas || ''); setCalidadAtencion(vendor.calidad_atencion || '');
  };

  const handleDeactivate = async (vendor) => {
    if (!window.confirm(`ELIMINAR PERMANENTEMENTE a ${vendor.nombre}? Esto borrará al vendedor y todos sus datos asociados.`)) return;
    try { await api.deleteVendor(vendor.id); toast.success('Vendedor eliminado permanentemente'); fetchVendors(); }
    catch (error) { toast.error('Error al eliminar vendedor'); }
  };

  const handleActivate = async (vendor) => {
    try { await api.activateVendor(vendor.id); toast.success('Vendedor reactivado'); fetchVendors(); }
    catch (error) { toast.error('Error al reactivar'); }
  };

  const handleUpdate = async () => {
    try {
      await api.updateVendor(editingVendor.id, { nombre: editNombre, ficha_casmu: editFicha, hora_entrada: horaEntrada, hora_descanso_salida: horaDescansoSalida, hora_descanso_vuelta: horaDescansoVuelta, hora_salida: horaSalida, conducta, llamadas, calidad_atencion: calidadAtencion });
      toast.success('Vendedor actualizado'); setEditingVendor(null); fetchVendors();
    } catch (error) { const detail = error.response?.data?.detail; toast.error(typeof detail === 'string' ? detail : 'Error al actualizar'); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!nombre || !apellido || !fichaCasmu) { toast.error('Completa todos los campos'); return; }
    try {
      const result = await api.createVendor({ nombre, apellido, ficha_casmu: fichaCasmu, hora_entrada: newHoraEntrada, hora_descanso_salida: newHoraDescansoSalida, hora_descanso_vuelta: newHoraDescansoVuelta, hora_salida: newHoraSalida });
      toast.success(result.message); setShowCreateDialog(false);
      setNombre(''); setApellido(''); setFichaCasmu(''); setNewHoraEntrada('08:00'); setNewHoraDescansoSalida('12:00'); setNewHoraDescansoVuelta('13:00'); setNewHoraSalida('17:00'); fetchVendors();
    } catch (error) { const detail = error.response?.data?.detail; toast.error(typeof detail === 'string' ? detail : 'Error al crear vendedor'); }
  };

  const formatHorario = (vendor) => {
    if (vendor.hora_entrada && vendor.hora_salida) return `${vendor.hora_entrada} - ${vendor.hora_salida}`;
    return vendor.horario || '-';
  };

  return (
    <div className="space-y-6" data-testid="vendedores">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#0A0A0A]">Vendedores</h1>
          <p className="text-[13px] text-gray-500 mt-0.5">Gestion del equipo de ventas</p>
        </div>
        <button onClick={() => setShowCreateDialog(true)} className="btn-primary flex items-center gap-2" data-testid="create-vendor-btn"><Plus size={16} /> Crear Vendedor</button>
      </div>

      <div className="mac-card p-4">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} className="form-input pl-10" placeholder="Buscar por nombre o ficha..." data-testid="vendor-search" />
        </div>
      </div>

      <div className="mac-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr><th className="table-header">Nombre</th><th className="table-header">Ficha</th><th className="table-header">Estado</th><th className="table-header">Horario</th><th className="table-header">Acciones</th></tr></thead>
            <tbody>
              {loading ? (<tr><td colSpan="5" className="table-cell text-center text-gray-500">Cargando...</td></tr>
              ) : vendors.length === 0 ? (<tr><td colSpan="5" className="table-cell text-center text-gray-500">No hay vendedores</td></tr>
              ) : (vendors.map(vendor => (
                <tr key={vendor.id} className={`table-row ${vendor.status === 'inactive' ? 'opacity-50' : ''}`}>
                  <td className="table-cell font-medium text-[#0A0A0A]">{vendor.nombre}</td>
                  <td className="table-cell">{vendor.ficha_casmu}</td>
                  <td className="table-cell">
                    {vendor.status === 'active' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#10B981]/10 text-[#10B981] text-[10px] font-medium"><UserCheck size={11} /> Activo</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#EF4444]/10 text-[#EF4444] text-[10px] font-medium"><UserX size={11} /> Baja</span>
                    )}
                  </td>
                  <td className="table-cell"><span className="text-[12px] text-gray-500">{formatHorario(vendor)}</span></td>
                  <td className="table-cell">
                    <div className="flex items-center gap-1">
                      <button onClick={() => openEditDialog(vendor)} className="btn-ghost p-1.5" data-testid={`edit-vendor-${vendor.id}`}><Pencil size={14} /></button>
                      {isAuditor && (<button onClick={() => handleDeactivate(vendor)} className="btn-ghost p-1.5 text-[#EF4444]" title="Eliminar permanentemente" data-testid={`delete-vendor-${vendor.id}`}><Trash2 size={14} /></button>)}
                    </div>
                  </td>
                </tr>
              )))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editingVendor} onOpenChange={(open) => !open && setEditingVendor(null)}>
        <DialogContent className="mac-popover max-w-md p-0">
          <DialogHeader className="px-5 py-4 border-b border-gray-200"><DialogTitle className="text-[15px] font-semibold text-[#0A0A0A]">Editar Vendedor</DialogTitle></DialogHeader>
          <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
            <div><label className="form-label">Nombre</label><input type="text" value={editNombre} onChange={(e) => setEditNombre(e.target.value)} className="form-input" data-testid="edit-nombre" /></div>
            <div><label className="form-label">Ficha CASMU</label><input type="text" value={editFicha} onChange={(e) => setEditFicha(e.target.value)} className="form-input" data-testid="edit-ficha" /></div>
            <div className="bg-gray-50 rounded-md p-4">
              <div className="flex items-center gap-2 mb-3"><Clock size={16} className="text-gray-400" /><span className="text-[13px] font-semibold text-gray-700">Horario</span></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="form-label">Entrada</label><input type="time" value={horaEntrada} onChange={(e) => setHoraEntrada(e.target.value)} className="form-input" data-testid="edit-hora-entrada" /></div>
                <div><label className="form-label">Salida Descanso</label><input type="time" value={horaDescansoSalida} onChange={(e) => setHoraDescansoSalida(e.target.value)} className="form-input" data-testid="edit-hora-descanso-salida" /></div>
                <div><label className="form-label">Vuelta Descanso</label><input type="time" value={horaDescansoVuelta} onChange={(e) => setHoraDescansoVuelta(e.target.value)} className="form-input" data-testid="edit-hora-descanso-vuelta" /></div>
                <div><label className="form-label">Salida</label><input type="time" value={horaSalida} onChange={(e) => setHoraSalida(e.target.value)} className="form-input" data-testid="edit-hora-salida" /></div>
              </div>
            </div>
            <div><label className="form-label">Conducta</label><textarea value={conducta} onChange={(e) => setConducta(e.target.value)} className="form-input min-h-[60px] resize-none" placeholder="Calificacion de conducta..." data-testid="edit-conducta" /></div>
            <div><label className="form-label">Llamadas</label><textarea value={llamadas} onChange={(e) => setLlamadas(e.target.value)} className="form-input min-h-[60px] resize-none" placeholder="Calificacion de llamadas..." data-testid="edit-llamadas" /></div>
            <div><label className="form-label">Calidad de Atencion</label><textarea value={calidadAtencion} onChange={(e) => setCalidadAtencion(e.target.value)} className="form-input min-h-[60px] resize-none" placeholder="Calificacion de calidad..." data-testid="edit-calidad" /></div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setEditingVendor(null)} className="btn-secondary">Cancelar</button>
              <button onClick={handleUpdate} className="btn-primary" data-testid="save-vendor-btn">Guardar</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="mac-popover max-w-md p-0">
          <DialogHeader className="px-5 py-4 border-b border-gray-200"><DialogTitle className="text-[15px] font-semibold text-[#0A0A0A]">Crear Vendedor</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="p-5 space-y-4">
            <div><label className="form-label">Ficha CASMU (sera el usuario)</label><input type="text" value={fichaCasmu} onChange={(e) => setFichaCasmu(e.target.value)} className="form-input" placeholder="Ej: 12345" data-testid="create-ficha" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="form-label">Nombre</label><input type="text" value={nombre} onChange={(e) => setNombre(e.target.value)} className="form-input" placeholder="Nombre" data-testid="create-nombre" /></div>
              <div><label className="form-label">Apellido</label><input type="text" value={apellido} onChange={(e) => setApellido(e.target.value)} className="form-input" placeholder="Apellido" data-testid="create-apellido" /></div>
            </div>
            <div className="bg-gray-50 rounded-md p-4">
              <div className="flex items-center gap-2 mb-3"><Clock size={16} className="text-gray-400" /><span className="text-[13px] font-semibold text-gray-700">Horario</span></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="form-label">Entrada</label><input type="time" value={newHoraEntrada} onChange={(e) => setNewHoraEntrada(e.target.value)} className="form-input" data-testid="create-hora-entrada" /></div>
                <div><label className="form-label">Salida Descanso</label><input type="time" value={newHoraDescansoSalida} onChange={(e) => setNewHoraDescansoSalida(e.target.value)} className="form-input" data-testid="create-hora-descanso-salida" /></div>
                <div><label className="form-label">Vuelta Descanso</label><input type="time" value={newHoraDescansoVuelta} onChange={(e) => setNewHoraDescansoVuelta(e.target.value)} className="form-input" data-testid="create-hora-descanso-vuelta" /></div>
                <div><label className="form-label">Salida</label><input type="time" value={newHoraSalida} onChange={(e) => setNewHoraSalida(e.target.value)} className="form-input" data-testid="create-hora-salida" /></div>
              </div>
            </div>
            <div className="bg-[#0082D2]/10 border border-[#0082D2]/20 rounded-md p-3 text-[12px] text-[#00205B]">
              La password del vendedor sera: <strong>casmu2026</strong>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowCreateDialog(false)} className="btn-secondary">Cancelar</button>
              <button type="submit" className="btn-primary" data-testid="save-new-vendor-btn">Crear Vendedor</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
